import "./pricingcalendar.css";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { toast } from "react-hot-toast";
import { useEffect, useState, useRef, useMemo } from "react";
import API from "src/API2";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcPlus     = () => <Svg><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Svg>;
const IcUpload   = () => <Svg><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></Svg>;
const IcCalendar = () => <Svg><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></Svg>;
const IcClose    = () => <Svg size={18}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IcTrash    = () => <Svg size={14}><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></Svg>;
const IcChart    = () => <Svg><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></Svg>;

/* ── Calendar preview modal ─────────────────────────────────────── */
const CalendarModal = ({ setOpen, occupancy }) => {
  const [events, setEvents] = useState();
  const localizer = momentLocalizer(moment);

  useEffect(() => {
    setEvents(
      (occupancy || []).map((e) => ({
        title: (+e.occupancy).toFixed(2) + " %",
        start: new Date(e.date),
        end: new Date(e.date),
      })),
    );
  }, [occupancy]);

  return (
    <div className="pc-modal-overlay" onClick={() => setOpen(false)}>
      <div className="pc-modal pc-modal--wide" onClick={(e) => e.stopPropagation()}>
        <header className="pc-modal__head">
          <div>
            <span className="pc-modal__eyebrow">Occupancy preview</span>
            <h3>Rate occupancy calendar</h3>
          </div>
          <button className="pc-icon-btn" onClick={() => setOpen(false)} aria-label="Close">
            <IcClose />
          </button>
        </header>
        <div className="pc-modal__body">
          <div className="pc-rbc">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ minHeight: 520 }}
              eventPropGetter={(event, start, end, isSelected) => {
                const style = {
                  backgroundColor: "#043263",
                  color: "#fff",
                  borderRadius: "6px",
                  border: "none",
                  minHeight: "60px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  outline: "none",
                  fontWeight: 600,
                  fontSize: 12,
                  letterSpacing: "0.3px",
                };
                if (isSelected) style.backgroundColor = "#16a34a";
                else if (end <= new Date()) {
                  style.backgroundColor = "#e5e7eb";
                  style.color = "#475569";
                } else if (start <= new Date() && new Date() <= end) {
                  style.backgroundColor = "#86efac";
                  style.color = "#064e3b";
                }
                return { style };
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Upload-confirmation modal ──────────────────────────────────── */
const UploadModal = ({ setOpen, json }) => {
  const post = () => {
    toast.loading("Updating occupancy table…");
    API.post("ratepricing/updateoccupancy", { date_occupancy: json })
      .then(() => {
        toast.dismiss();
        toast.success("Occupancy updated successfully");
        setOpen(false);
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Something went wrong — check your file format");
        setOpen(false);
      });
  };
  return (
    <div className="pc-modal-overlay" onClick={() => setOpen(false)}>
      <div className="pc-modal" onClick={(e) => e.stopPropagation()}>
        <header className="pc-modal__head">
          <div>
            <span className="pc-modal__eyebrow pc-modal__eyebrow--warn">Warning</span>
            <h3>Replace occupancy data?</h3>
          </div>
          <button className="pc-icon-btn" onClick={() => setOpen(false)} aria-label="Close">
            <IcClose />
          </button>
        </header>
        <div className="pc-modal__body">
          <p className="pc-modal__text">
            Updating the occupancy rates will affect availability and pricing for
            all rooms using these rates. This action is immediate and cannot be
            undone. Please double-check your data before continuing.
          </p>
        </div>
        <footer className="pc-modal__foot">
          <button className="pc-btn pc-btn--ghost" onClick={() => setOpen(false)}>Cancel</button>
          <button className="pc-btn pc-btn--danger" onClick={post}>Yes, update</button>
        </footer>
      </div>
    </div>
  );
};

/* ── Add-rule modal ─────────────────────────────────────────────── */
const Modal = ({ setOpen, setRatePricingRules }) => {
  const [minimumOccupancy, setMinimumOccupancy] = useState(0);
  const [maximumOccupancy, setMaximumOccupancy] = useState(100);
  const [rates, setRates] = useState();
  const [selectedRate, setSelectedRate] = useState("");

  useEffect(() => {
    API.get("rates").then((resp) => setRates(resp.data.rates));
  }, []);

  const post = () => {
    if (!selectedRate) { toast.error("Select a rate"); return; }
    toast.loading("Adding occupancy rule…");
    API.post("ratepricing", {
      rate_id: selectedRate,
      minimum_occupancy: minimumOccupancy,
      maximum_occupancy: maximumOccupancy,
    })
      .then((resp) => {
        toast.dismiss();
        toast.success("Pricing rule added");
        setRatePricingRules((prev) =>
          [...prev, resp.data.rate_rule].sort(
            (a, b) => a.minimum_occupancy - b.minimum_occupancy,
          ),
        );
        setTimeout(() => setOpen(false), 180);
      })
      .catch((error) => {
        toast.dismiss();
        toast.error(error?.response?.data?.message || "Could not add rule");
      });
  };

  return (
    <div className="pc-modal-overlay" onClick={() => setOpen(false)}>
      <div className="pc-modal" onClick={(e) => e.stopPropagation()}>
        <header className="pc-modal__head">
          <div>
            <span className="pc-modal__eyebrow">New rule</span>
            <h3>Add occupancy rule</h3>
          </div>
          <button className="pc-icon-btn" onClick={() => setOpen(false)} aria-label="Close">
            <IcClose />
          </button>
        </header>
        <div className="pc-modal__body">
          <label className="pc-field">
            <span>Rate</span>
            <select
              value={selectedRate}
              onChange={(e) => setSelectedRate(e.target.value)}
            >
              <option value="" disabled>Select rate</option>
              {rates?.map((e) => (
                <option key={e.id} value={e.id}>{e.code}</option>
              ))}
            </select>
          </label>

          <div className="pc-field-row">
            <label className="pc-field">
              <span>Minimum occupancy (%)</span>
              <input
                inputMode="numeric"
                value={minimumOccupancy}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^(100|[1-9]?[0-9])$/.test(v)) setMinimumOccupancy(v);
                }}
              />
            </label>
            <label className="pc-field">
              <span>Maximum occupancy (%)</span>
              <input
                inputMode="numeric"
                value={maximumOccupancy}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^(100|[1-9]?[0-9])$/.test(v)) setMaximumOccupancy(v);
                }}
              />
            </label>
          </div>
        </div>
        <footer className="pc-modal__foot">
          <button className="pc-btn pc-btn--ghost" onClick={() => setOpen(false)}>Cancel</button>
          <button className="pc-btn pc-btn--primary" onClick={post}>Add rule</button>
        </footer>
      </div>
    </div>
  );
};

/* ── Main screen ────────────────────────────────────────────────── */
const RateCalendar = () => {
  const [modalActive, setModalActive] = useState(false);
  const [uploadModalActive, setUploadModalActive] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [occupancy, setOccupancy] = useState([]);
  const [ratePricingRules, setRatePricingRules] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);

  const inputRef = useRef();

  useEffect(() => {
    setLoading(true);
    API.get("ratepricing")
      .then((resp) => setRatePricingRules(resp.data.rates_pricing_rules || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    API.get("ratepricing/updateoccupancy").then((resp) => {
      setOccupancy(resp.data.occupancy || []);
    }).catch(() => {});
  }, [file]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target.result);
        const clean = Object.entries(json).map(([date, occ]) => ({
          date,
          occupancy: occ,
        }));
        setFile(clean);
        setUploadModalActive(true);
      } catch {
        toast.error("Invalid JSON file");
      }
    };
    reader.readAsText(f);
  };

  const avgOccupancy = useMemo(() => {
    if (!occupancy.length) return null;
    const sum = occupancy.reduce((s, o) => s + Number(o.occupancy || 0), 0);
    return (sum / occupancy.length).toFixed(1);
  }, [occupancy]);

  return (
    <div className="pc-screen">
      {calendarOpen && <CalendarModal setOpen={setCalendarOpen} occupancy={occupancy} />}
      {uploadModalActive && <UploadModal setOpen={setUploadModalActive} json={file} />}
      {modalActive && <Modal setOpen={setModalActive} setRatePricingRules={setRatePricingRules} />}

      {/* ── Toolbar ── */}
      <div className="pc-toolbar">
        <div className="pc-toolbar__left">
          <div className="pc-mini-stat">
            <span className="pc-mini-stat__label">Active rules</span>
            <span className="pc-mini-stat__value">{ratePricingRules.length}</span>
          </div>
          <div className="pc-mini-stat">
            <span className="pc-mini-stat__label">Avg. occupancy</span>
            <span className="pc-mini-stat__value">
              {avgOccupancy !== null ? `${avgOccupancy}%` : "—"}
            </span>
          </div>
          <div className="pc-mini-stat">
            <span className="pc-mini-stat__label">Data points</span>
            <span className="pc-mini-stat__value">{occupancy.length}</span>
          </div>
        </div>
        <div className="pc-toolbar__right">
          <input
            type="file"
            accept=".json,application/json"
            ref={inputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <button className="pc-btn pc-btn--ghost" onClick={() => inputRef.current.click()}>
            <IcUpload /> Update occupancy
          </button>
          <button className="pc-btn pc-btn--ghost" onClick={() => setCalendarOpen(true)}>
            <IcCalendar /> Preview calendar
          </button>
          <button className="pc-btn pc-btn--primary" onClick={() => setModalActive(true)}>
            <IcPlus /> New rule
          </button>
        </div>
      </div>

      {/* ── Rules card ── */}
      <section className="pc-card">
        <header className="pc-card__head">
          <div>
            <h3>Occupancy-based rules</h3>
            <p>Rates adjust automatically based on live occupancy thresholds.</p>
          </div>
        </header>

        {loading ? (
          <div className="pc-empty">
            <IcChart /> <span>Loading rules…</span>
          </div>
        ) : ratePricingRules.length === 0 ? (
          <div className="pc-empty">
            <IcChart />
            <span>No occupancy rules yet.</span>
            <button className="pc-btn pc-btn--primary" onClick={() => setModalActive(true)}>
              <IcPlus /> Create your first rule
            </button>
          </div>
        ) : (
          <div className="pc-table-wrap">
            <table className="pc-table">
              <thead>
                <tr>
                  <th>Rate</th>
                  <th>Min. occupancy</th>
                  <th>Max. occupancy</th>
                  <th>Range</th>
                  <th className="pc-table__actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {ratePricingRules.map((rule) => {
                  const min = Number(rule.minimum_occupancy) || 0;
                  const max = Number(rule.maximum_occupancy) || 0;
                  return (
                    <tr key={rule.id}>
                      <td>
                        <span className="pc-rate-chip">{rule?.rate?.code}</span>
                      </td>
                      <td>{min}%</td>
                      <td>{max}%</td>
                      <td className="pc-range-cell">
                        <div className="pc-range-bar">
                          <span
                            className="pc-range-bar__fill"
                            style={{ left: `${min}%`, width: `${Math.max(1, max - min)}%` }}
                          />
                        </div>
                      </td>
                      <td className="pc-table__actions-col">
                        <button
                          className="pc-icon-btn pc-icon-btn--danger"
                          title="Double-click to delete"
                          onDoubleClick={() => {
                            API.delete(`ratepricing/${rule.id}`)
                              .then(() => {
                                setRatePricingRules((prev) =>
                                  prev.filter((f) => f.id !== rule.id),
                                );
                                toast.success("Rule deleted");
                              })
                              .catch(() => toast.error("Could not delete rule"));
                          }}
                        >
                          <IcTrash />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="pc-table__footnote">Double-click the trash icon to delete a rule.</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default RateCalendar;
